/**
 * Generic v1 BlockingTask detail sheet.
 *
 * Renders for any task type that hasn't yet shipped a type-specific
 * sheet (Plan 27 introduces the four real forms). Three blocks:
 *
 *   1. **Title + status badge.** The task's headline + an OPEN /
 *      IN_PROGRESS pill so the user knows whether someone else has
 *      already started.
 *
 *   2. **Field grid.** Category, subKind, due date, organization, the
 *      gated action slug. These are the universally-useful columns
 *      every task exposes — Plan 27's task-specific sheets layer
 *      richer interactions on top.
 *
 *   3. **Footer actions.** "Open in full UI" deep-links to the entity
 *      the task references (an order, a delivery, etc.); "Mark
 *      complete" dismisses with a stub reason. The deep-link target
 *      is computed from `blocksSubjectKind` + `blocksSubjectId` —
 *      `SHIPMENT` → `/cafeteria?shipment=<id>` etc. Unknown subjects
 *      hide the button rather than throw.
 *
 * This sheet is the **contract** that makes Plan 05 self-contained.
 * Even if Plan 27 never lands, every task remains actionable through
 * this fallback.
 */

import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'

import { type MyTaskRow, useDismissBlockingTaskMutation } from '@/api/tasks'

import { deepLinkForSubject, formatLabel, formatRelativeDate } from '../helpers'

// Re-export the pure helpers — Plan 05 split shaping logic out of
// this file into a sibling `helpers.ts` for test-runner ergonomics.
export { deepLinkForSubject, formatLabel }

export interface TaskSheetProps {
  task: MyTaskRow
  onClose: () => void
  /**
   * Fired when the task's work is actually finished (vs. Cancel/close).
   * The rollup wizard uses it to advance to the next campus; the plain
   * drawer leaves it unset and sheets fall back to `onClose`.
   */
  onCompleted?: () => void
}

export function GenericTaskDetailSheet({ task, onClose, onCompleted }: TaskSheetProps) {
  const dismissMutation = useDismissBlockingTaskMutation()

  const deepLink = deepLinkForSubject(task.blocksSubjectKind, task.blocksSubjectId)

  async function handleMarkComplete() {
    await dismissMutation.mutateAsync({
      id: task.id,
      reason: 'Resolved via task sheet — operator override',
    })
    ;(onCompleted ?? onClose)()
  }

  return (
    <div className="flex h-full flex-col gap-6">
      <header className="flex flex-col gap-2 pr-8">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold leading-tight">{task.title}</h2>
          {task.status === 'IN_PROGRESS' ? <Badge variant="secondary">In progress</Badge> : null}
        </div>
        {task.description ? <p className="text-sm text-muted-foreground">{task.description}</p> : null}
      </header>

      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Category" value={formatLabel(task.category)} />
        {task.subKind ? <Field label="Type" value={formatLabel(task.subKind)} /> : null}
        <Field
          label="Due"
          value={task.dueAt ? formatRelativeDate(task.dueAt) : 'No due date'}
          tone={task.dueAt && new Date(task.dueAt).getTime() < Date.now() ? 'destructive' : undefined}
        />
        <Field label="Organization" value={task.organization?.name ?? '—'} />
        <Field label="Blocks" value={task.blocksAction} mono className="sm:col-span-2" />
      </dl>

      <div className="mt-auto flex flex-col gap-2 sm:flex-row sm:justify-end">
        {deepLink ? (
          <Button asChild variant="outline">
            <a href={deepLink}>Open in full UI</a>
          </Button>
        ) : null}
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleMarkComplete} disabled={dismissMutation.isPending}>
          {dismissMutation.isPending ? 'Marking…' : 'Mark complete'}
        </Button>
      </div>
    </div>
  )
}

interface FieldProps {
  label: string
  value: string
  mono?: boolean
  tone?: 'destructive'
  className?: string
}

function Field({ label, value, mono, tone, className }: FieldProps) {
  return (
    <div className={className}>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd
        className={
          (mono ? 'font-mono text-xs ' : 'text-sm ') + (tone === 'destructive' ? 'text-destructive' : 'text-foreground')
        }>
        {value}
      </dd>
    </div>
  )
}
